import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "../api/client";
import { ConfirmDialog } from "../components/Dialog";
import { source, text } from "../text";
import { useCampaign, useCampaignQuery, useDraft } from "./context";
import { CampaignError, fieldText, useAction, useEntries, useSummary } from "./parts";
import type { CampaignDay, CampaignEntryListItem, CampaignInput, SituationEntry } from "./types";

/** The situation of the campaign in the current view: running adventures, scene with material, game day, party, threads. */
export function useSituation() {
  const { id } = useCampaign();
  return useCampaignQuery(["situation"], (headers) => api.GET("/api/campaigns/{id}/situation", { params: { path: { id } }, headers }));
}

/** The visible value of a field of an entry from a list or the situation. */
export function fieldOf(entry: Pick<CampaignEntryListItem | SituationEntry, "fields">, key: string): unknown {
  return entry.fields.find((f) => f.key === key)?.value ?? null;
}

/** The game day of a field with its date and its distance to today, as the API gives it. */
export function dayOf(entry: Pick<CampaignEntryListItem | SituationEntry, "fields">, key: string): CampaignDay | null {
  return entry.fields.find((f) => f.key === key)?.day ?? null;
}

/** The label of a field of a kind, e.g. the goal of a scene, as the kind defines it. */
export function useFieldLabel() {
  const campaign = useCampaign();
  return (kind: string, field: string) => campaign.kind(kind)?.fields.find((f) => f.key === field)?.label.de ?? field;
}

/** The label of a choice of a field, e.g. the status of an adventure, as the kind defines it. */
export function useChoiceLabel() {
  const campaign = useCampaign();
  return (kind: string, field: string, value: unknown) =>
    campaign.kind(kind)?.fields.find((f) => f.key === field)?.choices.find((c) => c.id === value)?.label.de ?? fieldText(value);
}

/** A deadline as the distance to the current game day. */
export function deadlineText(daysLeft: number | null | undefined): string {
  if (daysLeft == null) return "";
  if (daysLeft === 0) return "heute";
  if (daysLeft === 1) return "morgen";
  if (daysLeft > 1) return `in ${daysLeft} Tagen`;
  if (daysLeft === -1) return "seit gestern überfällig";
  return `seit ${-daysLeft} Tagen überfällig`;
}

/** Today in the browser's time zone as yyyy-mm-dd, the real date of a session. */
export function today(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export function realDate(value: unknown): string {
  if (typeof value !== "string") return "";
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}.${month}.${year}` : value;
}

export interface RevealTarget {
  target: "entry" | "section" | "field" | "attachment" | "link";
  id: string;
  field?: string;
  /** What is revealed, for the button's name, e.g. „Abschnitt Absichten“. */
  label: string;
  entryTitle: string;
  /** The game master view sees the entry only by its material parts; revealing one conceals its other known parts. */
  seenByParts?: boolean;
}

/**
 * Makes a secret part known with one tap. When the entry is seen only by its material, its head becomes known and its other
 * known parts become secret; the view says so before and asks.
 */
export function RevealButton({ reveal }: { reveal: RevealTarget }) {
  const campaign = useCampaign();
  const action = useAction();
  const [confirming, setConfirming] = useState(false);
  const payload: Record<string, unknown> = { target: reveal.target, id: reveal.id };
  if (reveal.field) payload.field = reveal.field;
  const apply = () => campaign.apply({ type: "campaign.reveal", payload });
  const concealing = campaign.mode === "normal" && !!reveal.seenByParts && reveal.target !== "entry";
  return (
    <>
      <button
        type="button"
        className="button secondary reveal"
        aria-label={`${reveal.label} aufdecken`}
        disabled={action.busy}
        onClick={() => (concealing ? setConfirming(true) : void action.run(apply))}
      >
        Aufdecken
      </button>
      <CampaignError error={action.error} onClose={action.clear} />
      {confirming && (
        <ConfirmDialog title="Aufdecken" action="Aufdecken" onClose={() => setConfirming(false)} onConfirm={apply}>
          <p>
            „{reveal.entryTitle}“ ist geheim, du siehst daraus nur das Szenenmaterial. Mit dem Aufdecken werden der Titel und {reveal.label} bekannt.
          </p>
          <p>
            Was an „{reveal.entryTitle}“ bisher als bekannt markiert war, wird dabei geheim, damit {campaign.term("player")} nur den Titel und diesen Teil sehen. Rückgängig
            nimmt beides zurück.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}

export type InputValues = Record<string, unknown>;

export function inputDefaults(inputs: CampaignInput[]): InputValues {
  return Object.fromEntries(inputs.map((i) => [i.key, i.default ?? (i.kind === "choice" ? (i.choices?.[0]?.id ?? null) : null)]));
}

/** An input a module asks for, such as part of the calendar anchor or an amount of a reward, with its hint and source. */
export function InputField(props: { input: CampaignInput; value: unknown; onChange: (value: unknown) => void; label?: string }) {
  const { input, value } = props;
  const label = props.label ?? input.label.de;
  const hint = input.hint ? [text(input.hint), (input.sources ?? []).map(source).filter(Boolean).join(", ")].filter(Boolean).join(" – ") : null;
  return (
    <label className="field">
      <span>{label}</span>
      {input.kind === "choice" ? (
        <select value={typeof value === "string" ? value : ""} onChange={(e) => props.onChange(e.target.value || null)}>
          {(input.choices ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.label.de}
            </option>
          ))}
        </select>
      ) : input.kind === "text" ? (
        <input value={typeof value === "string" ? value : ""} onChange={(e) => props.onChange(e.target.value)} />
      ) : (
        <input
          type="number"
          inputMode="numeric"
          min={input.min ?? undefined}
          max={input.max ?? undefined}
          value={typeof value === "number" || typeof value === "string" ? String(value) : ""}
          onChange={(e) => props.onChange(e.target.value === "" ? null : Number(e.target.value))}
        />
      )}
      {hint && <span className="muted small">{hint}</span>}
    </label>
  );
}

/** Who of the party attends; at first everyone. */
export function AttendeesField({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
  const campaign = useCampaign();
  if (campaign.head.party.length === 0) return null;
  return (
    <fieldset className="field">
      <legend className="muted small">Anwesend</legend>
      {campaign.head.party.map((p) => (
        <label key={p.id} className="option">
          <input type="checkbox" checked={value.includes(p.id)} onChange={(e) => onChange(e.target.checked ? [...value, p.id] : value.filter((x) => x !== p.id))} />
          {p.name}
        </label>
      ))}
    </fieldset>
  );
}

/** Starts a session: title, real date, attendees from the party and the adventure it belongs to. */
export function BeginSessionDialog({ onClose, onBegun, notice }: { onClose: () => void; onBegun?: () => void; notice?: string }) {
  const campaign = useCampaign();
  const adventures = useEntries({ kind: "adventure" });
  const open = (adventures.data ?? []).filter((a) => fieldOf(a, "status") !== "completed");
  const running = open.filter((a) => fieldOf(a, "status") === "running");
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(today);
  const [attendees, setAttendees] = useState(() => campaign.head.party.map((p) => p.id));
  const [adventure, setAdventure] = useState<string | null>(null);
  const chosen = adventure ?? (running.length === 1 ? running[0]!.id : "");
  const session = campaign.term("session");
  useDraft(title.trim() !== "" || adventure !== null);
  return (
    <ConfirmDialog
      title={`${session} beginnen`}
      action="Beginnen"
      onClose={onClose}
      onConfirm={() => {
        const payload: Record<string, unknown> = { date, attendees };
        if (title.trim()) payload.title = title.trim();
        if (chosen) payload.adventureId = chosen;
        return campaign.apply({ type: "campaign.session.begin", payload }).then(() => onBegun?.());
      }}
    >
      {notice && <p>{notice}</p>}
      <label className="field">
        <span>Titel</span>
        <input value={title} placeholder={`${session} am ${realDate(date)}`} onChange={(e) => setTitle(e.target.value)} autoFocus />
      </label>
      <label className="field">
        <span>Datum</span>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <AttendeesField value={attendees} onChange={setAttendees} />
      {open.length > 0 && (
        <label className="field">
          <span>{campaign.term("adventure")}</span>
          <select value={chosen} onChange={(e) => setAdventure(e.target.value)}>
            <option value="">–</option>
            {open.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title}
              </option>
            ))}
          </select>
        </label>
      )}
    </ConfirmDialog>
  );
}

/** Closes the running session with its summary, which players see; the active scene ends with it. */
export function CloseSessionDialog(props: { sessionId: string; title: string; onClose: () => void }) {
  const campaign = useCampaign();
  const current = useSummary(props.sessionId);
  const [summary, setSummary] = useState<string | null>(null);
  const value = summary ?? current.section?.text ?? "";
  useDraft(summary !== null && summary !== (current.section?.text ?? ""));
  return (
    <ConfirmDialog
      title={`${campaign.term("session")} abschließen`}
      action="Abschließen"
      onClose={props.onClose}
      onConfirm={() => campaign.apply({ type: "campaign.session.close", payload: { id: props.sessionId, summary: value } })}
    >
      <p>
        „{props.title}“ wird abgeschlossen.{campaign.head.play.sceneId ? ` Was als ${campaign.term("scene")} aktiv ist, endet dabei.` : ""}
      </p>
      <label className="field">
        <span>Zusammenfassung (für alle sichtbar)</span>
        <textarea rows={6} value={value} onChange={(e) => setSummary(e.target.value)} autoFocus />
      </label>
    </ConfirmDialog>
  );
}

/**
 * Begins a scene: one already prepared, or a new one with title, goal and place. A field that is secret by default, like the goal,
 * can only be written in the dossier, so outside it the dialog leaves it out and says so.
 */
export function BeginSceneDialog({ onClose }: { onClose: () => void }) {
  const campaign = useCampaign();
  const label = useFieldLabel();
  const scenes = (useEntries({ kind: "scene" }).data ?? []).filter((s) => s.id !== campaign.head.play.sceneId);
  const places = useEntries({ kind: "place" }).data ?? [];
  const [existing, setExisting] = useState("");
  const [title, setTitle] = useState("");
  const [goal, setGoal] = useState("");
  const [place, setPlace] = useState("");
  const scene = campaign.term("scene");
  const goalSecret = campaign.kind("scene")?.fields.find((f) => f.key === "goal")?.defaultVisibility === "secret";
  const withGoal = campaign.mode === "dossier" || !goalSecret;
  useDraft(existing !== "" || title.trim() !== "" || goal.trim() !== "" || place !== "");
  return (
    <ConfirmDialog
      title={`${scene} beginnen`}
      action="Beginnen"
      onClose={onClose}
      onConfirm={async () => {
        if (existing) return campaign.apply({ type: "campaign.scene.begin", payload: { id: existing } });
        if (!title.trim()) throw new Error("Ohne Titel lässt sich nichts beginnen.");
        const fields: Record<string, unknown> = {};
        if (withGoal && goal.trim()) fields.goal = goal.trim();
        if (place) fields.place = place;
        return campaign.apply({ type: "campaign.scene.begin", payload: { title: title.trim(), fields } });
      }}
    >
      {scenes.length > 0 && (
        <label className="field">
          <span>Vorbereitet</span>
          <select value={existing} onChange={(e) => setExisting(e.target.value)}>
            <option value="">Neu anlegen</option>
            {scenes.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </label>
      )}
      {!existing && (
        <>
          <label className="field">
            <span>Titel</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          </label>
          {withGoal && (
            <label className="field">
              <span>{label("scene", "goal")}</span>
              <input value={goal} onChange={(e) => setGoal(e.target.value)} />
            </label>
          )}
          {places.length > 0 && (
            <label className="field">
              <span>{campaign.kindLabel("place")}</span>
              <select value={place} onChange={(e) => setPlace(e.target.value)}>
                <option value="">–</option>
                {places.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </label>
          )}
          <p className="muted small">
            {campaign.mode === "dossier" ? "Im Dossier angelegt ist der Eintrag geheim." : "Der Eintrag ist bekannt."}
            {!withGoal && ` Das Feld „${label("scene", "goal")}“ ist geheim; eintragen lässt es sich im Dossier.`}
          </p>
        </>
      )}
    </ConfirmDialog>
  );
}

/** A note at the table into the log of the running session (F11); without one it offers to begin a session first. */
export function NoteDialog({ onClose }: { onClose: () => void }) {
  const campaign = useCampaign();
  const [note, setNote] = useState("");
  const begun = useRef(false);
  const session = campaign.term("session");
  useDraft(note.trim() !== "");
  if (!campaign.head.play.sessionId)
    return (
      <BeginSessionDialog
        notice={`${session}: Gerade läuft nichts, was du hier siehst. Die Notiz gehört ins Protokoll des laufenden Eintrags; beginne ihn hier. Läuft einer, der nur im Dossier sichtbar ist, schließe ihn dort ab.`}
        onBegun={() => (begun.current = true)}
        onClose={() => !begun.current && onClose()}
      />
    );
  return (
    <ConfirmDialog
      title="Notiz"
      action="Notieren"
      onClose={onClose}
      onConfirm={() => {
        if (!note.trim()) throw new Error("Die Notiz ist leer.");
        return campaign.apply({ type: "campaign.session.note", payload: { text: note } });
      }}
    >
      <label className="field">
        <span>Notiz</span>
        <textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
      </label>
      <p className="muted small">
        Die Notiz geht ins geheime Protokoll des laufenden Eintrags ({session}); {campaign.term("player")} sehen sie nicht. Zurücknehmen lässt sie sich nur im
        Dossier.
      </p>
    </ConfirmDialog>
  );
}

/** A person created at the table: known in the normal view, secret in the dossier. */
export function NewPersonDialog({ onClose }: { onClose: () => void }) {
  const campaign = useCampaign();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const person = campaign.kindLabel("person");
  useDraft(title.trim() !== "");
  return (
    <ConfirmDialog
      title={`${person} anlegen`}
      action="Anlegen"
      onClose={onClose}
      onConfirm={async () => {
        if (!title.trim()) throw new Error("Der Eintrag braucht einen Titel.");
        const result = await campaign.apply({ type: "campaign.entry.create", payload: { kind: "person", title: title.trim() } });
        if (result.created) navigate(campaign.entryPath(result.created));
      }}
    >
      <label className="field">
        <span>Name</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
      </label>
      <p className="muted small">{campaign.mode === "dossier" ? "Im Dossier angelegt ist der Eintrag geheim." : "Der Eintrag ist bekannt."}</p>
    </ConfirmDialog>
  );
}

/** Sets the game day to a number; day 1 is the anchor of the calendar. */
export function SetDayDialog({ onClose }: { onClose: () => void }) {
  const campaign = useCampaign();
  const [day, setDay] = useState(String(campaign.head.play.day));
  const term = campaign.term("day");
  return (
    <ConfirmDialog
      title={`${term} setzen`}
      action="Setzen"
      onClose={onClose}
      onConfirm={() => {
        const value = Number(day);
        if (!Number.isInteger(value) || value < 1) throw new Error(`${term}: bitte eine ganze Zahl ab 1.`);
        return campaign.apply({ type: "campaign.day", payload: { action: "set", day: value } });
      }}
    >
      <label className="field">
        <span>{term}</span>
        <input type="number" min={1} step={1} value={day} onChange={(e) => setDay(e.target.value)} autoFocus />
      </label>
      <p className="muted small">{term} 1 ist der Anker des Kalenders aus den Einstellungen.</p>
    </ConfirmDialog>
  );
}

/** Sets or clears where the party is. */
export function SetPlaceDialog({ onClose }: { onClose: () => void }) {
  const campaign = useCampaign();
  const places = useEntries({ kind: "place" }).data ?? [];
  const [place, setPlace] = useState(campaign.head.play.placeId ?? "");
  return (
    <ConfirmDialog
      title={`${campaign.term("party")}: ${campaign.kindLabel("place")}`}
      action="Setzen"
      onClose={onClose}
      onConfirm={() => campaign.apply({ type: "campaign.place", payload: place ? { placeId: place } : {} })}
    >
      {places.length === 0 ? (
        <p className="muted">Noch kein Eintrag der Art „{campaign.kindLabel("place")}“.</p>
      ) : (
        <label className="field">
          <span>{campaign.kindLabel("place")}</span>
          <select value={place} onChange={(e) => setPlace(e.target.value)} autoFocus>
            <option value="">unbekannt</option>
            {places.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </label>
      )}
    </ConfirmDialog>
  );
}
